import { useCallback, useEffect, useMemo, useState } from "react"

import { applySelectedChanges } from "../optimization/applyChanges"
import type { OptimizationResult } from "../optimization/optimization"

export function usePromptOptimizationSelection({
  currentPrompt,
  optimizationResult,
}: {
  currentPrompt: string
  optimizationResult: OptimizationResult | null
}) {
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useState<"list" | "combined">("list")

  useEffect(() => {
    if (optimizationResult && optimizationResult.changes.length > 0) {
      setAcceptedIds(
        new Set(optimizationResult.changes.map((change) => change.id)),
      )
      setViewMode("list")
    }
  }, [optimizationResult])

  const toggleChange = useCallback((changeId: string) => {
    setAcceptedIds((previous) => {
      const next = new Set(previous)
      if (next.has(changeId)) {
        next.delete(changeId)
      } else {
        next.add(changeId)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    if (optimizationResult) {
      setAcceptedIds(
        new Set(optimizationResult.changes.map((change) => change.id)),
      )
    }
  }, [optimizationResult])

  const clearSelection = useCallback(() => {
    setAcceptedIds(new Set())
  }, [])

  const combinedPreview = useMemo(() => {
    if (!optimizationResult || acceptedIds.size === 0) {
      return currentPrompt
    }
    return applySelectedChanges(
      currentPrompt,
      optimizationResult.changes,
      Array.from(acceptedIds),
    )
  }, [acceptedIds, currentPrompt, optimizationResult])

  return {
    acceptedIds,
    clearSelection,
    combinedPreview,
    selectAll,
    setViewMode,
    toggleChange,
    viewMode,
  }
}
